import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from '@/app/error/ErrorBoundary';
import { AppProviders } from '@/app/providers/AppProviders';
import { refreshTokens } from '@/features/auth/api/refreshTokens';
import { useAuthStore } from '@/features/auth/store/authStore';
import { setLogoutHandler, setRefreshHandler } from '@/lib/api/client';
import { toast } from '@/lib/notify';
import { App } from './App';
import './styles/globals.css';

// Register the auth-feature refresh handler with the API client.
setRefreshHandler(refreshTokens);

// Register the revocation-logout handler. When the backend returns
// Auth.MembershipRevoked (403), the API client clears the session and shows a
// toast. ProtectedRoute then redirects to the auth service on the next render.
setLogoutHandler((reason) => {
  toast.error(reason);
  useAuthStore.getState().clearSession();
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

const root = createRoot(rootElement);

function render(): void {
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <AppProviders>
            <App />
          </AppProviders>
        </BrowserRouter>
      </ErrorBoundary>
    </StrictMode>,
  );
}

// The in-memory access token is empty on a cold load / new tab; the refresh token
// lives in the HttpOnly sc_refresh cookie. Block the first render on a silent
// refresh so a logged-in user isn't bounced to the OAuth login redirect.
void refreshTokens().finally(render);
